import { useState, useCallback, useRef, useEffect } from 'react';

import ReactPlayer from 'react-player';

type UseVideoPlayerProps = {
  autoPlay?: boolean;
  controlsLocked?: boolean;
  onReady?: () => void;
  onError?: (error: unknown) => void;
};

export const useVideoPlayer = ({
  autoPlay = true,
  controlsLocked = false,
  onReady,
  onError,
}: UseVideoPlayerProps = {}) => {
  const playerRef = useRef<ReactPlayer>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasPlayedRef = useRef(false);
  const controlsLockedRef = useRef(controlsLocked);

  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(autoPlay);
  const [isAtLiveEdge, setIsAtLiveEdge] = useState(true);
  const [volume, setVolume] = useState(0.8);
  const [played, setPlayed] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Keep ref in sync with prop
  useEffect(() => {
    controlsLockedRef.current = controlsLocked;
    if (controlsLocked) {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    }
  }, [controlsLocked]);

  // Play/Pause
  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const play = useCallback(() => {
    setIsPlaying(true);
  }, []);

  // Seek to live edge and resume playing
  const seekToLive = useCallback(() => {
    playerRef.current?.seekTo(1, 'fraction');
    setIsPlaying(true);
    setIsAtLiveEdge(true);
  }, []);

  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Volume
  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const handleVolumeChange = useCallback((newVolume: number) => {
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  }, []);

  // Progress
  const handleProgress = useCallback(
    (state: { played: number; loaded: number }) => {
      if (!isSeeking) {
        setPlayed(state.played);
        setLoaded(state.loaded);
      }
    },
    [isSeeking],
  );

  // Seek
  const handleSeekStart = useCallback(() => {
    setIsSeeking(true);
  }, []);

  const handleSeek = useCallback((value: number) => {
    setPlayed(value);
  }, []);

  const handleSeekEnd = useCallback((value: number) => {
    setIsSeeking(false);
    playerRef.current?.seekTo(value, 'fraction');
  }, []);

  // Duration
  const handleDuration = useCallback((dur: number) => {
    setDuration(dur);
  }, []);

  // Buffering
  const handleBuffer = useCallback(() => {
    setIsBuffering(true);
  }, []);

  const handleBufferEnd = useCallback(() => {
    setIsBuffering(false);
  }, []);

  // Ready
  const handleReady = useCallback(() => {
    setIsReady(true);
    onReady?.();
  }, [onReady]);

  // Unmute after playback starts (bypass browser autoplay restriction)
  const handleStart = useCallback(() => {
    if (autoPlay && !hasPlayedRef.current) {
      hasPlayedRef.current = true;
      setTimeout(() => {
        setIsMuted(false);
      }, 1500);
    }
  }, [autoPlay]);

  // Error
  const handleError = useCallback(
    (error: unknown) => {
      console.error('VideoPlayer error:', error);
      onError?.(error);
    },
    [onError],
  );

  // Fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  }, []);

  // Controls visibility
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    if (isPlaying && !controlsLockedRef.current) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  const handleMouseMove = useCallback(() => {
    showControlsTemporarily();
  }, [showControlsTemporarily]);

  const handleMouseLeave = useCallback(() => {
    if (isPlaying && !controlsLockedRef.current) {
      setShowControls(false);
    }
  }, [isPlaying]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if container is focused or fullscreen
      if (
        !containerRef.current?.contains(document.activeElement) &&
        !isFullscreen
      ) {
        return;
      }

      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          togglePlay();
          break;
        case 'f':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'm':
          e.preventDefault();
          toggleMute();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          playerRef.current?.seekTo(
            Math.max(0, played - 10 / duration),
            'fraction',
          );
          break;
        case 'ArrowRight':
          e.preventDefault();
          playerRef.current?.seekTo(
            Math.min(1, played + 10 / duration),
            'fraction',
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          handleVolumeChange(Math.min(1, volume + 0.1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          handleVolumeChange(Math.max(0, volume - 0.1));
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    togglePlay,
    toggleFullscreen,
    toggleMute,
    handleVolumeChange,
    played,
    duration,
    volume,
    isFullscreen,
  ]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Show controls when paused + mark off live edge
  useEffect(() => {
    if (!isPlaying) {
      setShowControls(true);
      setIsAtLiveEdge(false);
    }
  }, [isPlaying]);

  // Cleanup
  useEffect(
    () => () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    },
    [],
  );

  // Format time helper
  const formatTime = useCallback((seconds: number) => {
    if (!seconds || !Number.isFinite(seconds)) return '0:00';

    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return {
    // Refs
    playerRef,
    containerRef,

    // State
    isPlaying,
    isMuted,
    volume,
    played,
    loaded,
    duration,
    isBuffering,
    isFullscreen,
    showControls,
    isReady,
    isAtLiveEdge,

    // Actions
    togglePlay,
    play,
    pause,
    seekToLive,
    toggleMute,
    handleVolumeChange,
    handleProgress,
    handleSeekStart,
    handleSeek,
    handleSeekEnd,
    handleDuration,
    handleBuffer,
    handleBufferEnd,
    handleReady,
    handleStart,
    handleError,
    toggleFullscreen,

    // Mouse handlers
    handleMouseMove,
    handleMouseLeave,

    // Helpers
    formatTime,
    currentTime: played * duration,
  };
};

export default useVideoPlayer;
